import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Trash2, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { packageTypeService } from '../../services/cityService';

const DEFAULT_DURATIONS = [1, 2, 3, 5, 10];
const COMMON_CHARGE_KEYS = ['eChannel', 'eChannelRenewal', 'includedActivities', 'additionalActivityFee'];
const KNOWN_CHARGE_KEYS = new Set(COMMON_CHARGE_KEYS);
const CHARGE_TYPE_OPTIONS = [
  { value: '', label: 'No type (known key)' },
  { value: 'flat_fee', label: 'Initial Fee' },
  { value: 'flat_renewal_fee', label: 'Renewal Fee' },
];

const toNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return 0;
};

const createFeeMap = (durations) =>
  durations.reduce((acc, duration) => {
    acc[duration] = 0;
    return acc;
  }, {});

const createEmptyPackage = (durations = DEFAULT_DURATIONS) => ({
  visa: 0,
  establishmentCard: 0,
  establishmentCardRenewal: 0,
  licenseFee: createFeeMap(durations),
  discountedFee: createFeeMap(durations),
});

const normalizePackage = (pkg) => {
  const licenseFee = {};
  const discountedFee = {};

  if (pkg?.licenseFee && typeof pkg.licenseFee === 'object') {
    Object.entries(pkg.licenseFee).forEach(([year, amount]) => {
      licenseFee[Number(year)] = toNumber(amount);
    });
  }

  if (pkg?.discountedFee && typeof pkg.discountedFee === 'object') {
    Object.entries(pkg.discountedFee).forEach(([year, amount]) => {
      discountedFee[Number(year)] = toNumber(amount);
    });
  }

  return {
    visa: toNumber(pkg?.visa),
    establishmentCard: toNumber(pkg?.establishmentCard),
    establishmentCardRenewal: toNumber(pkg?.establishmentCardRenewal),
    licenseFee,
    discountedFee,
  };
};

const normalizeAdditionalCharges = (additionalCharges, chargeConfig = {}) => {
  if (!additionalCharges || typeof additionalCharges !== 'object') {
    return [];
  }

  return Object.entries(additionalCharges)
    .filter(([key]) => Boolean(key))
    .map(([key, value]) => {
      const config = chargeConfig[key] || {};
      return {
        key,
        amount: toNumber(value),
        type: config.type || '',
        label: config.label || '',
      };
    });
};

const PricingStructureForm = ({
  isOpen,
  onClose,
  onSubmit,
  freezoneId,
  freezoneName,
  freezoneCode,
  editingData = null,
}) => {
  const [packageType, setPackageType] = useState('');
  const [packages, setPackages] = useState([]);
  const [additionalCharges, setAdditionalCharges] = useState([]);
  const [packageTypeOptions, setPackageTypeOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const resolvedFreezoneCode = String(freezoneCode || freezoneName || '').trim().toUpperCase();
  const isShams = resolvedFreezoneCode === 'SHAMS';

  const allDurations = useMemo(() => {
    const yearSet = new Set(DEFAULT_DURATIONS);
    packages.forEach((pkg) => {
      Object.keys(pkg.licenseFee || {}).forEach((year) => yearSet.add(Number(year)));
      Object.keys(pkg.discountedFee || {}).forEach((year) => yearSet.add(Number(year)));
    });

    return Array.from(yearSet)
      .filter((year) => Number.isFinite(year))
      .sort((a, b) => a - b);
  }, [packages]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const loadPackageTypes = async () => {
      try {
        const response = await packageTypeService.getPackageTypes();
        const types = response?.package_types || response?.data || response || [];
        const normalized = Array.isArray(types)
          ? types
              .map((type) => String(type?.name || '').trim())
              .filter(Boolean)
          : [];

        const defaults = ['Standard', 'Media', 'General'];
        const merged = Array.from(new Set([...defaults, ...normalized]));
        setPackageTypeOptions(merged);
      } catch (error) {
        console.error('Error loading package types:', error);
        setPackageTypeOptions(['Standard', 'Media', 'General']);
      }
    };

    loadPackageTypes();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!editingData) {
      setPackageType(isShams ? 'standard' : '');
      setPackages([createEmptyPackage()]);
      setAdditionalCharges([]);
      return;
    }

    const normalizedPackages = (editingData.packages || []).map(normalizePackage);
    const safePackages = normalizedPackages.length > 0 ? normalizedPackages : [createEmptyPackage()];

    setPackageType(editingData.licenseType || (isShams ? 'standard' : ''));
    setPackages(safePackages);
    setAdditionalCharges(normalizeAdditionalCharges(editingData.additionalCharges, editingData.chargeConfig || {}));
  }, [editingData, isOpen, isShams]);

  const addPackage = () => {
    setPackages((prev) => [...prev, createEmptyPackage(allDurations)]);
  };

  const removePackage = (index) => {
    setPackages((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePackageField = (index, field, value) => {
    setPackages((prev) =>
      prev.map((pkg, i) => (i === index ? { ...pkg, [field]: toNumber(value) } : pkg))
    );
  };

  const updateFee = (index, feeKey, duration, value) => {
    setPackages((prev) =>
      prev.map((pkg, i) => {
        if (i !== index) {
          return pkg;
        }

        return {
          ...pkg,
          [feeKey]: {
            ...pkg[feeKey],
            [duration]: toNumber(value),
          },
        };
      })
    );
  };

  const addAdditionalCharge = (key = '') => {
    const isKnown = KNOWN_CHARGE_KEYS.has(key);
    setAdditionalCharges((prev) => [...prev, { key, amount: 0, type: isKnown ? '' : 'flat_fee', label: '' }]);
  };

  const updateAdditionalCharge = (index, field, value) => {
    setAdditionalCharges((prev) =>
      prev.map((charge, i) => {
        if (i !== index) {
          return charge;
        }

        return {
          ...charge,
          [field]: field === 'amount' ? toNumber(value) : value,
        };
      })
    );
  };

  const removeAdditionalCharge = (index) => {
    setAdditionalCharges((prev) => prev.filter((_, i) => i !== index));
  };

  const packageRows = useMemo(
    () =>
      packages
        .map((pkg, index) => ({ pkg, index }))
        .sort((a, b) => a.pkg.visa - b.pkg.visa),
    [packages]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!resolvedFreezoneCode && !freezoneId) {
      toast.error('Freezone is missing for this pricing entry.');
      return;
    }

    if (isShams && !packageType) {
      toast.error('Package type is required for SHAMS pricing.');
      return;
    }

    if (packages.length === 0) {
      toast.error('Add at least one visa package.');
      return;
    }

    const visaSet = new Set();
    for (const pkg of packages) {
      if (visaSet.has(pkg.visa)) {
        toast.error('Visa counts must be unique per package set.');
        return;
      }
      visaSet.add(pkg.visa);
    }

    const payload = {
      freezoneId: String(resolvedFreezoneCode || freezoneId || '').toUpperCase(),
      licenseType: isShams ? String(packageType).toLowerCase() : undefined,
      packages: [...packages]
        .sort((a, b) => a.visa - b.visa)
        .map((pkg) => ({
        visa: toNumber(pkg.visa),
        licenseFee: Object.entries(pkg.licenseFee || {}).reduce((acc, [year, amount]) => {
          acc[Number(year)] = toNumber(amount);
          return acc;
        }, {}),
        discountedFee: Object.entries(pkg.discountedFee || {}).reduce((acc, [year, amount]) => {
          acc[Number(year)] = toNumber(amount);
          return acc;
        }, {}),
        establishmentCard: toNumber(pkg.establishmentCard),
        establishmentCardRenewal: toNumber(pkg.establishmentCardRenewal),
        })),
      additionalCharges: additionalCharges.reduce((acc, charge) => {
        const key = String(charge.key || '').trim();
        if (!key) {
          return acc;
        }
        acc[key] = toNumber(charge.amount);
        return acc;
      }, {}),
      chargeConfig: additionalCharges.reduce((acc, charge) => {
        const key = String(charge.key || '').trim();
        if (!key || !charge.type) {
          return acc;
        }
        acc[key] = {
          type: charge.type,
          label: charge.label || key,
        };
        return acc;
      }, {}),
    };

    setLoading(true);
    try {
      await onSubmit(payload);
      toast.success(editingData ? 'Pricing updated' : 'Pricing created');
      onClose();
    } catch (error) {
      console.error('Error submitting pricing structure:', error);
      toast.error('Failed to save pricing structure');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div className="max-h-[92vh] w-full max-w-7xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {editingData ? 'Edit Pricing Set' : 'Create Pricing Set'}
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Freezone: <span className="font-semibold text-slate-900">{String(resolvedFreezoneCode || freezoneId || '').toUpperCase()}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-700">Pricing Scope</h3>
            {isShams ? (
              <div className="max-w-sm">
                <label className="mb-1 block text-sm font-medium text-slate-700">Package Type</label>
                <select
                  value={packageType}
                  onChange={(e) => setPackageType(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  required
                >
                  <option value="">Select package type</option>
                  {packageTypeOptions.map((type) => (
                    <option key={type} value={String(type).toLowerCase()}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="text-sm text-slate-600">This freezone uses a single default package set (no package type split).</p>
            )}
          </section>

          <section className="rounded-xl border border-slate-200 p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Visa Packages</h3>
                <p className="text-sm text-slate-600">Configure fees by visa count and license duration.</p>
              </div>
              <button
                type="button"
                onClick={addPackage}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                <Plus size={16} />
                Add Visa Tier
              </button>
            </div>

            <div className="space-y-4">
              {packageRows.map(({ pkg, index: packageIndex }) => (
                <div key={`pkg-${packageIndex}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-800">{pkg.visa} Visa Package</h4>
                    <button
                      type="button"
                      onClick={() => removePackage(packageIndex)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-700"
                      disabled={packageRows.length === 1}
                    >
                      <Trash2 size={14} />
                      Remove
                    </button>
                  </div>

                  <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600">Visa Count</label>
                      <input
                        type="number"
                        min="0"
                        value={pkg.visa}
                        onChange={(e) => updatePackageField(packageIndex, 'visa', e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600">Establishment Card</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={pkg.establishmentCard}
                        onChange={(e) => updatePackageField(packageIndex, 'establishmentCard', e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600">Est. Card Renewal</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={pkg.establishmentCardRenewal}
                        onChange={(e) => updatePackageField(packageIndex, 'establishmentCardRenewal', e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="rounded-l-lg px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Duration</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Regular Fee</th>
                          <th className="rounded-r-lg px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Discounted Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allDurations.map((duration) => (
                          <tr key={`${packageIndex}-${duration}`} className="border-b border-slate-100 last:border-none">
                            <td className="px-3 py-2 text-sm font-medium text-slate-700">{duration} year{duration > 1 ? 's' : ''}</td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={pkg.licenseFee?.[duration] ?? 0}
                                onChange={(e) => updateFee(packageIndex, 'licenseFee', duration, e.target.value)}
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={pkg.discountedFee?.[duration] ?? 0}
                                onChange={(e) => updateFee(packageIndex, 'discountedFee', duration, e.target.value)}
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Additional Charges</h3>
                <p className="text-sm text-slate-600">Set extra fees consumed by company formation checkout logic.</p>
              </div>
              <button
                type="button"
                onClick={() => addAdditionalCharge('')}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
              >
                <Plus size={16} />
                Add Charge
              </button>
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              {COMMON_CHARGE_KEYS.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => addAdditionalCharge(key)}
                  className="rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
                >
                  + {key}
                </button>
              ))}
            </div>

            {additionalCharges.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-sm text-slate-600">
                No additional charges configured.
              </p>
            ) : (
              <div className="space-y-3">
                {additionalCharges.map((charge, index) => {
                  const isKnown = KNOWN_CHARGE_KEYS.has(charge.key);
                  return (
                    <div key={`charge-${index}`} className="rounded-lg border border-slate-200 p-3 space-y-2">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_160px_auto]">
                        <input
                          type="text"
                          value={charge.key}
                          onChange={(e) => updateAdditionalCharge(index, 'key', e.target.value)}
                          placeholder="Charge key (e.g. eChannel)"
                          className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={charge.amount}
                          onChange={(e) => updateAdditionalCharge(index, 'amount', e.target.value)}
                          placeholder="Amount"
                          className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeAdditionalCharge(index)}
                          className="inline-flex items-center justify-center rounded-lg border border-rose-200 px-3 py-2 text-rose-600 transition hover:bg-rose-50"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      {!isKnown && (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <select
                            value={charge.type || ''}
                            onChange={(e) => updateAdditionalCharge(index, 'type', e.target.value)}
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                          >
                            {CHARGE_TYPE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={charge.label || ''}
                            onChange={(e) => updateAdditionalCharge(index, 'label', e.target.value)}
                            placeholder="Display label (optional)"
                            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <Save size={16} />
              {loading ? 'Saving...' : editingData ? 'Update Pricing' : 'Create Pricing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PricingStructureForm;
